import { useEffect, useSyncExternalStore } from 'react';

export function isDemoStudioEnabled(hostname = typeof window !== 'undefined' ? window.location.hostname : ''): boolean {
  const host = hostname.toLowerCase();
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === 'ldg-hub-staging.netlify.app' ||
    (host.startsWith('deploy-preview-') && host.endsWith('.netlify.app'))
  );
}

/**
 * TEMPORARY (2026-08-11, Bobbie: existing testers must not have to verify
 * again after the move to hub.thestudioldg.com): waive only the client-side
 * verification wall on the two Hub testing hosts. Keep isDemoStudioEnabled
 * narrower so the custom domain does NOT inherit role preview, registration
 * shortcuts, or any other demo-only control.
 *
 * Signing in still requires a real Google/password account, and NOTHING
 * server-side changes here: Firestore rules and Netlify Functions keep their
 * own role/status/email-verification gates. Remove the custom-domain entry
 * when Bobbie ends tester access and begins real-family onboarding.
 */
export function emailVerificationWaived(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
): boolean {
  const host = hostname.toLowerCase();
  return isDemoStudioEnabled(host) || host === 'hub.thestudioldg.com';
}

// ---------------------------------------------------------------------------
// Parent preview on REAL families
// ---------------------------------------------------------------------------

/**
 * The family a Director is currently looking at in the parent view.
 *
 * Why this exists: the parent preview used to require the Director's own uid
 * to be linked to a DEMO dancer, so it only worked while fake data was in the
 * database. Bobbie wanted the fake data gone and the preview kept, which are
 * the same job. She now picks a real family instead.
 *
 * Why it is deliberately hemmed in:
 *   - sessionStorage, not localStorage: a preview cannot outlive the tab, and
 *     nothing carries over to the next time the Hub is opened.
 *   - Picking is always an explicit choice on the picker card. Nothing sets
 *     this as a side effect of opening a page or tapping a nav tile.
 *   - It is READ ONLY. Every action on the previewed screen refuses to write
 *     while this is set (see FamilyCommandCenter). Writing as a parent who
 *     never saw the message would turn the studio's acknowledgement record
 *     into a lie, so the block sits on the action, not on the button.
 *   - It never becomes an identity. It is not fed to the role override, so no
 *     other screen in the app ever believes the Director is this family.
 */
export interface PreviewedFamily {
  /** The real signed-in Director who deliberately opened this preview. */
  ownerUid: string;
  uid: string;
  /** Plain-words name for the banner, e.g. "the Parker family". */
  label: string;
}

const PREVIEW_FAMILY_KEY = 'ldg-preview-family';
const PREVIEW_FAMILY_EVENT = 'ldg-preview-family-change';

/** What the banner and every blocked action say, in one place. */
export const FAMILY_PREVIEW_READ_ONLY =
  'This is a read-only look at a family account. Nothing can be acknowledged, replied to, or marked done from here, because it would be recorded as though the family did it.';

/**
 * Routes the preview survives navigating to (2026-08-19: Bobbie, "once I get
 * there, I can't click within it and I really need to be able to click to
 * see what their calendar looks like and their classes look like"). Command
 * Center was the only screen wired for this at first; Calendar and Classes
 * joined it. Every other route reads the signed-in Director's own auth, so
 * previewing there would show the Director's data or nothing under the
 * family's name instead of an honest refusal.
 *
 * Single source of truth for two different jobs, so they cannot drift into
 * two different lists:
 *   - GlobalChrome.tsx clears the preview on any route-change that lands
 *     OUTSIDE this set.
 *   - FamilyCommandCenter.tsx's own read-only lock (goTo / onLockedAnchor /
 *     SourceButton) lets a tap on Calendar or Classes WALK to that page with
 *     the preview intact, instead of refusing it - those are exactly the
 *     "click within it" doors Bobbie asked for. Every other locked door on
 *     that page (tasks, messages, a class page, a dancer profile) still
 *     refuses, because navigating there would either clear the preview on
 *     arrival (GlobalChrome) or show the Director's own data under the
 *     family's name.
 */
export const FAMILY_PREVIEW_ROUTES = new Set([
  '/family/command-center',
  '/family/calendar',
  '/family/classes',
]);

/**
 * True when `href` (a query string or #hash fragment stripped first) is one
 * of FAMILY_PREVIEW_ROUTES. Used by FamilyCommandCenter's locked doors to
 * decide whether a tap should walk through the preview or refuse it.
 */
export function familyPreviewRouteAllowsWalk(href: string): boolean {
  const path = href.split(/[?#]/)[0];
  return FAMILY_PREVIEW_ROUTES.has(path);
}

export function getPreviewedFamily(): PreviewedFamily | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PREVIEW_FAMILY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PreviewedFamily>;
    if (
      typeof parsed?.ownerUid !== 'string' ||
      !parsed.ownerUid ||
      typeof parsed.uid !== 'string' ||
      !parsed.uid
    ) return null;
    return {
      ownerUid: parsed.ownerUid,
      uid: parsed.uid,
      label: typeof parsed.label === 'string' ? parsed.label : 'this family',
    };
  } catch {
    return null;
  }
}

export function setPreviewedFamily(family: PreviewedFamily | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (family) window.sessionStorage.setItem(PREVIEW_FAMILY_KEY, JSON.stringify(family));
    else window.sessionStorage.removeItem(PREVIEW_FAMILY_KEY);
  } catch {
    // Storage blocked: the preview just does not survive a page change.
  }
  window.dispatchEvent(new Event(PREVIEW_FAMILY_EVENT));
}

function subscribePreviewedFamily(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(PREVIEW_FAMILY_EVENT, callback);
  return () => window.removeEventListener(PREVIEW_FAMILY_EVENT, callback);
}

// Re-parsing on every read would hand React a new object each time and spin
// useSyncExternalStore, so the parsed value is cached until it changes.
let cachedRaw: string | null = null;
let cachedFamily: PreviewedFamily | null = null;

function previewedFamilySnapshot(): PreviewedFamily | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(PREVIEW_FAMILY_KEY);
  } catch {
    raw = null;
  }
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedFamily = getPreviewedFamily();
  }
  return cachedFamily;
}

/**
 * Subscribe to this Director's previewed family.
 *
 * sessionStorage belongs to a browser tab, not to a Firebase account. The
 * owner check prevents a shared-device sign-out/sign-in from carrying one
 * Director's selected family into another Director's session. Once the new
 * account is known, discard the mismatched record as well as hiding it.
 */
export function usePreviewedFamily(ownerUid: string | null | undefined): PreviewedFamily | null {
  const family = useSyncExternalStore(subscribePreviewedFamily, previewedFamilySnapshot, () => null);
  const ownedFamily = ownerUid && family?.ownerUid === ownerUid ? family : null;

  useEffect(() => {
    if (ownerUid && family && family.ownerUid !== ownerUid) setPreviewedFamily(null);
  }, [family, ownerUid]);

  return ownedFamily;
}
